import React from 'react'
import { Container, Row, Button, Card } from 'react-bootstrap'
import { AccordionSection, AccordionCard } from '../../accordion'
import {
  formatCurrency,
  formatNumber,
  IsSpecialEmployer,
} from 'application/helpers'
import { Tabs } from '@mmsg/louie'
import ContactUsDialog from '../../dialog/contactUsDialog'
import { applicationRoles } from 'application/constants/actionTypes'
import * as E from './runningSectionElements'
import * as S from './estimateDetailElements'
import { EstimateDetailStaticData } from './estimateDetailStaticData'
import { PrimaryLink } from 'application/components/styledComponents/link'
import FeesAndChargesSection from './feesAndChargesSection'
import ColItem from './colItem'
import RegoAndInsurance from './regoAndInsuranceSection'
import TaxImplicationsSection from './taxImplicationsSection'

const RunningCostTable = ({
  fuelPay,
  electricityPay,
  tyresPay,
  roadsidePay,
  roadsideAssist,
  maintenancePay,
  incidentalsPay,
  noOfTyresBudget,
  fuelUseSubtext,
  electricityUseSubtext,
  maintenanceSubtext,
  tyresSubtext,
  roadsideSubtext,
  id,
  isExGST,
}) => (
  <Row>
    <ColItem
      icon="Fuel"
      id={`${id}-fuel`}
      title="Fuel"
      value={fuelPay}
      tooltip={{
        subHeading: fuelUseSubtext,
        text: 'Taking into account a few factors from your lease (like your estimated kms or preferred car), we set aside a budget for you to keep filling up your car with fuel.',
      }}
      isExGST={isExGST}
    />
    <ColItem
      icon="Electricity"
      id={`${id}-electricity`}
      title="Electricity"
      value={electricityPay}
      tooltip={{
        subHeading: electricityUseSubtext,
        text: 'Taking into account a few factors from your lease (like your estimated kms or preferred car), we set aside a budget for you to keep your car’s battery charged.',
      }}
      isExGST={isExGST}
    />
    <ColItem
      icon="ScheduledMaintenance"
      id={`${id}-scheduled-maintenance`}
      title="Scheduled servicing"
      value={maintenancePay}
      tooltip={{
        subHeading: maintenanceSubtext,
        text: 'Covers your standard servicing at manufacturer’s set intervals, either according to time or kilometres travelled.',
      }}
      isExGST={isExGST}
    />
    <ColItem
      icon={global.config.isBrandRS ? 'ReplacementTyres' : 'Tyres'}
      id={`${id}-replacement-tyres`}
      title={`${noOfTyresBudget} Replacement tyres`}
      value={tyresPay}
      tooltip={{
        subHeading: tyresSubtext,
        text: 'Taking into account a few factors from your lease (like your estimated kms or preferred car), we set aside a budget for replacing worn out tyres. Generally, you’ll need a new set for every 40,000kms you travel.',
      }}
      isExGST={isExGST}
    />
    <ColItem
      icon="Incidentals"
      id={`${id}-incidentals`}
      title="Incidentals"
      value={incidentalsPay}
      tooltip={{
        text: 'A budget for those extra costs, like getting your car cleaned or detailed, or even topping up your oil.',
      }}
      isExGST={isExGST}
    />
    {roadsideAssist && (
      <ColItem
        icon="RoadsideAssistance"
        id={`${id}-roadside`}
        title={
          global.config.isBrandMX ? 'Roadside assist' : 'Roadside assistance'
        }
        value={roadsidePay}
        tooltip={{
          subHeading: roadsideSubtext,
          text: '24/7 access to trained mechanical experts, helping get you back on the road in the event of a breakdown.',
        }}
        isExGST={isExGST}
      />
    )}
  </Row>
)

const RunningSection = ({
  data: {
    brand,
    isExGST,
    isTaxShare,
    fbtMethod,
    payFrequency,
    kmPerAnnum,
    userFirstName,
    consultantFirstName,
    costsPerPay,
    fuelPerPay,
    electricityPerPay,
    tyresPerPay,
    roadsideAssist,
    roadsidePerPay,
    maintenancePerPay,
    incidentalsPerPay,
    fuelPerAnnum,
    electricityPerAnnum,
    tyresPerAnnum,
    roadsidePerAnnum,
    maintenancePerAnnum,
    incidentalsPerAnnum,
    regoInsPerPay,
    regoPerPay,
    regoPerAnnum,
    compInsPerPay,
    compInsPerAnnum,
    totalChargesPerPay,
    nlFeePerPay,
    spFeePerPay,
    fbtPerPay,
    lcdaPerPay,
    erSharePerPay,
    gstOnECMPerPay,
    greenfleetPerPay,
    erAdminFeePerPay,
    nlFeePerAnnum,
    spFeePerAnnum,
    fbtPerAnnum,
    lcdaPerAnnum,
    erSharePerAnnum,
    gstOnECMPerAnnum,
    greenfleetPerAnnum,
    erAdminFeePerAnnum,
    noOfTyresBudget,
    overTheCapMethod,
    compInsuranceProvider,
    vehicle,
    isGov,
    fuelPricePerLitre,
    electricityCostPerKm,
    leaseTermYears,
    compInsurancePremium,
    compInsurancePremiumFinanced
  },
  applicationRole,
  expandAccordion,
}) => {
  if (roadsideAssist && (roadsidePerPay === 0 || roadsidePerAnnum === 0)) {
    roadsidePerPay = 'Free'
    roadsidePerAnnum = 'Free'
  }

  let fuelUseSubtext =
    vehicle && vehicle.id
      ? isGov &&
        `(${vehicle.fuelConsumption} litres/100kms @ ${
          fuelPricePerLitre && fuelPricePerLitre > 0
            ? '$' + fuelPricePerLitre + ' per litre'
            : null
        }) (${vehicle.fuelType})`
      : null

  let electricityUseSubtext =
    isGov && electricityCostPerKm
      ? `(Estimated at ${
          electricityCostPerKm && (electricityCostPerKm * 100).toFixed(1)
        }c per km)`
      : null

  let compInsuranceSubtext =
    isGov && compInsurancePremium > 0 && '(RemServ arranged)'
  let registrationSubtext =
    isGov && `(${leaseTermYears - 1} renewals budgeted for term of lease)`
  let replacementTyresSubtext =
    isGov && `(${noOfTyresBudget} budgeted for term of lease)`

  let isSAGBYO = IsSpecialEmployer('SAGBYO')
  let budgetText = "We’ve estimated a budget for your bills and running costs. "
  if(!isSAGBYO)
    budgetText += "And with a fully maintained lease, it’ll be our job to manage it. "
  budgetText += "By rolling all your running costs into your novated lease, not only could you access tax savings, but you’ll avoid bill shock when things like registration roll around."

  return (
    <Container className="section print-page-break-before mt-5">
      <S.StyledH3 className="mb-4">
        Here’s your budget for keeping your car on the road
      </S.StyledH3>
      <p>
        {budgetText}
      </p>
      {/* Running costs and maintenance: Start */}
      <Card className="mb-13rem card-shadow">
        <Card.Header className="border-bottom-0 d-flex justify-content-between">
          <S.CardHeader>Running costs and servicing</S.CardHeader>
          <S.CardHeaderValue id="runningsection.runningcosts">
            {formatCurrency(costsPerPay)}
          </S.CardHeaderValue>
        </Card.Header>
        <Card.Body>
          <AccordionSection
            readerText="Running costs and servicing"
            expandAccordion={expandAccordion}
            title="Flexible budgeting for your fuel, scheduled servicing and more. It’s always your money. You choose how you spend it and when."
            id="runningsection.budgetingaccordion"
          >
            <S.BrandDefaultText
              style={{
                fontWeight: global.config.isBrandRS ? 'bold' : 'normal',
              }}
            >
              You’ve estimated you’ll drive
            </S.BrandDefaultText>
            <E.AnnualKmText id="runningsection.annualkm">
              {formatNumber(kmPerAnnum)} km/year
            </E.AnnualKmText>
            {global.config.isBrandRS && (
              <S.CardHeaderValue>How it works:</S.CardHeaderValue>
            )}
            <S.GreyTextDiv className="mb-3 ml-0">
              {EstimateDetailStaticData(isSAGBYO ? 'HowItWorks_SAGBYO': 'HowItWorks')}
            </S.GreyTextDiv>
            <Tabs
              id="runningsection.runningcosttab"
              data={[
                {
                  title: payFrequency,
                  content: () => (
                    <RunningCostTable
                      fuelPay={fuelPerPay}
                      electricityPay={electricityPerPay}
                      tyresPay={tyresPerPay}
                      roadsidePay={roadsidePerPay}
                      roadsideAssist={roadsideAssist}
                      maintenancePay={maintenancePerPay}
                      incidentalsPay={incidentalsPerPay}
                      id={`running-perPay`}
                      noOfTyresBudget={noOfTyresBudget}
                      fuelUseSubtext={fuelUseSubtext}
                      electricityUseSubtext={electricityUseSubtext}
                      roadsideSubtext={
                        isGov && !roadsideAssist ? '(Client arranged)' : ''
                      }
                      maintenanceSubtext={
                        isGov &&
                        "(As per manufacturer's scheduled logbook servicing)"
                      }
                      tyresSubtext={replacementTyresSubtext}
                      isExGST={isExGST}
                    />
                  ),
                },
                {
                  title: 'Annually',
                  content: () => (
                    <RunningCostTable
                      fuelPay={fuelPerAnnum}
                      electricityPay={electricityPerAnnum}
                      tyresPay={tyresPerAnnum}
                      roadsidePay={roadsidePerAnnum}
                      roadsideAssist={roadsideAssist}
                      maintenancePay={maintenancePerAnnum}
                      incidentalsPay={incidentalsPerAnnum}
                      id={`running-Annually`}
                      noOfTyresBudget={noOfTyresBudget}
                      fuelUseSubtext={fuelUseSubtext}
                      electricityUseSubtext={electricityUseSubtext}
                      roadsideSubtext={
                        isGov && !roadsideAssist ? '(Client arranged)' : ''
                      }
                      maintenanceSubtext={
                        isGov &&
                        "(As per manufacturer's scheduled logbook servicing)"
                      }
                      tyresSubtext={replacementTyresSubtext}
                      isExGST={isExGST}
                    />
                  ),
                },
              ]}
              activeIndex={0}
              equalWidth
            />

            {((!fuelPerPay.incGST && !fuelPerPay.exGST) ||
              (!maintenancePerPay.incGST && !maintenancePerPay.exGST) ||
              (!tyresPerPay.incGST && !tyresPerPay.exGST) ||
              (!incidentalsPerPay.incGST && !incidentalsPerPay.exGST) ||
              (!roadsidePerPay.incGST && !roadsidePerPay.exGST)) && (
              <>
                <p className="font-weight-bold text-brand mb-2">
                  Running costs you’ve excluded
                </p>
                <p className="mb-4">
                  Based on your chat with {consultantFirstName}, here’s what
                  you’ve excluded. Don’t forget your budget is flexible{' '}
                  {userFirstName}, and you’ll get back whatever you don’t spend.
                  So, if you’d like to add anything back in, simply{' '}
                  {global.config.isBrandMX &&
                    applicationRole &&
                    applicationRole !== applicationRoles.CONSULTANT && (
                      <>
                        <ContactUsDialog
                          className="link"
                          theme="link"
                          launchText="Contact your consultant"
                          id="runningsection.contactconsultant1"
                        />
                        .
                      </>
                    )}
                  {global.config.isBrandMX &&
                    applicationRole &&
                    applicationRole === applicationRoles.CONSULTANT && (
                      <>
                        <a
                          className="link text-underline readonly"
                          theme="link"
                          id="runningsection.contactconsultant1readonly"
                        >
                          Contact your consultant
                        </a>
                        .
                      </>
                    )}
                  {global.config.isBrandRS && 'Contact your consultant.'}
                </p>

                <ul>
                  {!fuelPerPay.incGST && !fuelPerPay.exGST && <li>Fuel</li>}
                  {!maintenancePerPay.incGST && !maintenancePerPay.exGST && (
                    <li>Scheduled servicing</li>
                  )}
                  {!tyresPerPay.incGST && !tyresPerPay.exGST && (
                    <li>Replacement tyres</li>
                  )}
                  {!incidentalsPerPay.incGST && !incidentalsPerPay.exGST && (
                    <li>Incidentals</li>
                  )}
                  {!roadsidePerPay.incGST && !roadsidePerPay.exGST && (
                    <li>Roadside assistance</li>
                  )}
                </ul>
              </>
            )}
            {global.config.isBrandMX &&
              applicationRole &&
              applicationRole !== applicationRoles.CONSULTANT && (
                <ContactUsDialog
                  className="mb-4 print hide-to-print"
                  theme="button"
                  size="sm"
                  launchText="Contact my consultant"
                  extendInMob
                  id="runningsection.contactconsultant2"
                />
              )}
            {global.config.isBrandMX &&
              applicationRole &&
              applicationRole === applicationRoles.CONSULTANT && (
                <Button
                  className="mb-4 readonly print hide-to-print"
                  theme="button"
                  size="sm"
                  id="runningsection.contactconsultant2readonly"
                >
                  Contact my consultant
                </Button>
              )}

            {global.config.isBrandRS &&
              applicationRole &&
              applicationRole !== applicationRoles.CONSULTANT && (
                <ContactUsDialog
                  className="link font-weight-bold text-underline mb-3 text-dark"
                  theme="link"
                  launchText="Contact my consultant"
                  inlineText
                />
              )}
            {global.config.isBrandRS &&
              applicationRole &&
              applicationRole === applicationRoles.CONSULTANT && (
                <PrimaryLink disabled>Contact my consultant</PrimaryLink>
              )}

            <AccordionCard
              expandAccordion={expandAccordion}
              className="mb-24"
              title="What if I have money left in my account at the end of my lease?"
              id="runningsection.remainingfundsaccordion"
              smallerTitleText={global.config.isBrandRS}
            >
              <S.BrandDefaultText>
                {EstimateDetailStaticData(isSAGBYO ? 'WhatIfIHaveMoneyLeft_SAGBYO' : 'WhatIfIHaveMoneyLeft')}
              </S.BrandDefaultText>
            </AccordionCard>
            <AccordionCard
              expandAccordion={expandAccordion}
              className="mb-24"
              title="What happens if I do more kilometres than estimated?"
              id="runningsection.morekmaccordion"
              smallerTitleText={global.config.isBrandRS}
            >
              <S.BrandDefaultText>
                {isSAGBYO ? 'Please contact your salary packaging provider to enquire adjusting your estimated kilometres and budgets.' : "Not to worry, your budget is flexible. So, if you do more kilometres than you estimated, your consultant can adjust your budget to keep up. Drive less? We'll pass back the savings from whatever you don't spend once your lease wraps up."}
              </S.BrandDefaultText>
            </AccordionCard>
            <AccordionCard
              expandAccordion={expandAccordion}
              className="mb-24"
              title="What is not included in a novated lease?"
              id="runningsection.excludedaccordion"
              smallerTitleText={global.config.isBrandRS}
            >
              <S.BrandDefaultText>
                {EstimateDetailStaticData('NotIncluded')}
              </S.BrandDefaultText>
            </AccordionCard>
            <AccordionCard
              expandAccordion={expandAccordion}
              className="mb-24"
              title="What happens if I'm involved in an accident?"
              id="runningsection.accidentaccordion"
              smallerTitleText={global.config.isBrandRS}
            >
              <S.BrandDefaultText>
                If the car can be fixed, we'll guide you through two options:
                <br />
                <br />
                1. Organise the repairs through your insurer; or – <br />
                2. If it's cheaper, we can organise the repairs directly with a
                repair centre.
                <br />
                <br />
                If the car is written off by your Comprehensive Motor Vehicle
                insurer, the insurer will either replace the car (subject to
                their terms and conditions) or pay your financier the market
                value of the car. In some cases this can still leave you out of
                pocket - particularly if the outstanding finance is higher than
                the car's market value.
                {!isSAGBYO && <div>
                  <br />
                  <br />
                  If you opt to purchase our Total Loss Assist Insurance, it can
                  cover the gap between you comprehensive insurers total loss
                  payment and either the outstanding finance amount or what you
                  paid for the vehicle. The minimum amount you receive under this
                  additional insurance if you are eligible to claim, is $5000.
                  <br />
                  <br />
                  Add-on insurances are available through {global.config.brand},
                  or from the provider of your choice, and can be packaged when
                  you start your lease. Please speak with your consultant about
                  adding this to your budget.
                </div>
                }
              </S.BrandDefaultText>
            </AccordionCard>
          </AccordionSection>
        </Card.Body>
      </Card>

      <RegoAndInsurance
        regoInsPerPay={regoInsPerPay}
        payFrequency={payFrequency}
        regoPerPay={regoPerPay}
        compInsPerPay={compInsPerPay}
        regoPerAnnum={regoPerAnnum}
        compInsPerAnnum={compInsPerAnnum}
        registrationSubtext={registrationSubtext}
        compInsuranceSubtext={compInsuranceSubtext}
        id="rego-compIns"
        isExGST={isExGST}
        compInsuranceProvider={compInsuranceProvider}
        compInsurancePremiumFinanced={compInsurancePremiumFinanced}
      />

      <FeesAndChargesSection
        nlFeePerPay={nlFeePerPay}
        spFeePerPay={spFeePerPay}
        nlFeePerAnnum={nlFeePerAnnum}
        spFeePerAnnum={spFeePerAnnum}
        isExGST={isExGST}
        payFrequency={payFrequency}
        expandAccordion={false}
      />

      <TaxImplicationsSection
        fbtPerPay={fbtPerPay}
        lcdaPerPay={lcdaPerPay}
        isTaxShare={isTaxShare}
        erSharePerPay={erSharePerPay}
        gstOnECMPerPay={gstOnECMPerPay}
        greenfleetPerPay={greenfleetPerPay}
        erAdminFeePerPay={erAdminFeePerPay}
        fbtPerAnnum={fbtPerAnnum}
        lcdaPerAnnum={lcdaPerAnnum}
        erSharePerAnnum={erSharePerAnnum}
        gstOnECMPerAnnum={gstOnECMPerAnnum}
        greenfleetPerAnnum={greenfleetPerAnnum}
        erAdminFeePerAnnum={erAdminFeePerAnnum}
        isExGST={isExGST}
        fbtMethod={fbtMethod}
        overTheCapMethod={overTheCapMethod}
        payFrequency={payFrequency}
      />
    </Container>
  )
}

export default RunningSection
